import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Package, UserPlus, Check, Upload, FileDown, PencilLine } from "lucide-react";

const BUS_TYPES = ["Para Transit", "20ft Transit", "30ft Transit", "40ft Transit", "60ft Transit", "Coach Bus"];
const ENERGY_SOURCES = ["Gas", "Diesel", "Electric", "Hybrid", "Hydro"];
const STATUSES = ["Not Started", "Active", "In Progress", "Pending", "Completed", "Others"];

const EMPTY_BUS = { bus_number: "", vin: "", bus_type: "40ft Transit", manufacturer: "", energy_source: "Diesel" };
const EMPTY_CUST = {
  first_name: "", last_name: "", business_name: "", phone: "", email: "",
  address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "",
};

export default function CreateProject() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState({
    name: "", company_id: "", pre_prep_required: "No", status: "Not Started",
    estimated_completion_date: "", start_date: "", end_date: "",
    labor_fee_per_unit: "150", part_rate: "400",
  });
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [busMode, setBusMode] = useState("manual"); // manual | csv
  const [rows, setRows] = useState([{ ...EMPTY_BUS }]);
  const [saving, setSaving] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(EMPTY_CUST);
  const [savingCust, setSavingCust] = useState(false);

  const loadCompanies = () => api.get("/companies").then((r) => setCompanies(r.data));
  useEffect(() => { loadCompanies(); api.get("/technicians").then((r) => setTechs(r.data)); }, []);

  const shipped = Math.ceil(rows.length * 1.1);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCust = (k, v) => setNewCust((c) => ({ ...c, [k]: v }));
  const toggleTech = (id) => setSelectedTechs((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const update = (i, key, val) => { const c = [...rows]; c[i][key] = val; setRows(c); };
  const addRow = () => setRows([...rows, { ...EMPTY_BUS }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const downloadTemplate = () => {
    const csv = "Bus Number,VIN,Bus Type,Manufacturer,Bus Energy Source\nBUS-001,1HGCM82633A004352,40ft Transit,New Flyer,Diesel\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bus_manifest_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) { toast.error(t("createProject.csvEmpty")); return; }
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (name) => header.indexOf(name);
    const iNum = idx("bus number"), iVin = idx("vin"), iType = idx("bus type"), iMan = idx("manufacturer"), iEng = idx("bus energy source");
    if (iNum < 0 || iVin < 0) { toast.error(t("createProject.csvNoRequired")); return; }
    const parsed = lines.slice(1).map((line) => {
      const cols = line.split(",").map((c) => c.trim());
      return {
        bus_number: cols[iNum] || "",
        vin: iVin >= 0 ? cols[iVin] || "" : "",
        bus_type: iType >= 0 ? cols[iType] || "" : "",
        manufacturer: iMan >= 0 ? cols[iMan] || "" : "",
        energy_source: iEng >= 0 ? cols[iEng] || "" : "",
      };
    }).filter((r) => r.bus_number || r.vin);
    if (!parsed.length) { toast.error(t("createProject.csvNoRows")); return; }
    setRows(parsed);
    toast.success(t("createProject.csvLoaded", { count: parsed.length }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const quickCustomer = async () => {
    if (!newCust.business_name) return toast.error(t("createProject.businessNameRequired"));
    setSavingCust(true);
    try {
      const { data } = await api.post("/companies", { ...newCust });
      if (data.supervisor_credentials)
        toast.success(t("createProject.custSupCreated", { email: data.supervisor_credentials.email, password: data.supervisor_credentials.password }), { duration: 8000 });
      else toast.success(t("createProject.custCreated"));
      await loadCompanies();
      set("company_id", data.id);
      setCustOpen(false);
      setNewCust(EMPTY_CUST);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingCust(false); }
  };

  const submit = async () => {
    if (!form.name) return toast.error("Project name is required");
    if (!form.company_id) return toast.error("Select a customer");
    if (rows.some((r) => !r.bus_number?.trim() || !r.vin?.trim()))
      return toast.error("Every bus needs a Bus Number and VIN");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        name: form.name,
        company_id: form.company_id,
        pre_prep_required: form.pre_prep_required === "Yes",
        status: form.status,
        estimated_completion_date: form.estimated_completion_date || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        technician_ids: selectedTechs,
        labor_fee_per_unit: parseFloat(form.labor_fee_per_unit) || 0,
        part_rate: parseFloat(form.part_rate) || 0,
        manifest: rows.map((r) => ({
          bus_number: r.bus_number,
          vin: r.vin,
          bus_type: r.bus_type || "",
          manufacturer: r.manufacturer || "",
          energy_source: r.energy_source || "",
        })),
      });
      toast.success(t("createProject.projectCreated", { id: data.id, count: data.parts_shipped }));
      navigate(`/projects/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="create-project-page">
      <div>
        <h1 className="font-heading text-3xl font-black text-slate-900">{t("createProject.title")}</h1>
        <p className="mt-1 text-slate-500">{t("createProject.subtitle")}</p>
      </div>

      <Card className="border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="overline text-slate-500">{t("createProject.projectName")}</Label>
            <Input data-testid="project-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("createProject.projectNamePlaceholder")} className="mt-1.5 border-slate-300 bg-white" />
          </div>
          <div>
            <Label className="overline text-slate-500">{t("createProject.prePrepRequired")}</Label>
            <Select value={form.pre_prep_required} onValueChange={(v) => set("pre_prep_required", v)}>
              <SelectTrigger data-testid="project-preprep" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">{t("createProject.yes")}</SelectItem><SelectItem value="No">{t("createProject.no")}</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">{t("createProject.customer")}</Label>
            <div className="mt-1.5 flex gap-2">
              <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                <SelectTrigger data-testid="project-company" className="border-slate-300 bg-white"><SelectValue placeholder={t("createProject.selectCustomer")} /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" data-testid="quick-add-customer" variant="outline" size="icon" onClick={() => setCustOpen(true)} className="shrink-0 border-slate-300"><UserPlus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div>
            <Label className="overline text-slate-500">{t("createProject.status")}</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="project-status" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">{t("createProject.eta")}</Label>
            <Input data-testid="project-eta" type="date" value={form.estimated_completion_date} onChange={(e) => set("estimated_completion_date", e.target.value)} className="mt-1.5 border-slate-300 bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="overline text-slate-500">{t("createProject.startDate")}</Label><Input data-testid="project-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            <div><Label className="overline text-slate-500">{t("createProject.endDate")}</Label><Input data-testid="project-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
          </div>
          <div><Label className="overline text-slate-500">{t("createProject.laborFee")}</Label><Input data-testid="project-labor" type="number" value={form.labor_fee_per_unit} onChange={(e) => set("labor_fee_per_unit", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
          <div><Label className="overline text-slate-500">{t("createProject.partRate")}</Label><Input data-testid="project-partrate" type="number" value={form.part_rate} onChange={(e) => set("part_rate", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
        </div>

        <div className="mt-4">
          <Label className="overline text-slate-500">{t("createProject.technicians")}</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="tech-multiselect">
            {techs.length === 0 && <span className="text-sm text-slate-400">{t("createProject.noTechs")}</span>}
            {techs.map((t) => {
              const on = selectedTechs.includes(t.id);
              return (
                <button key={t.id} type="button" data-testid={`tech-chip-${t.id}`} onClick={() => toggleTech(t.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-blue-400"}`}>
                  {on && <Check className="h-3.5 w-3.5" />} {t.name}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold text-slate-900">{t("createProject.buses")}</h2>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-300 p-0.5">
              <button type="button" data-testid="bus-mode-manual" onClick={() => setBusMode("manual")}
                className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors ${busMode === "manual" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <PencilLine className="h-4 w-4" /> {t("createProject.manuallyAdd")}
              </button>
              <button type="button" data-testid="bus-mode-csv" onClick={() => setBusMode("csv")}
                className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors ${busMode === "csv" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Upload className="h-4 w-4" /> {t("createProject.uploadCsv")}
              </button>
            </div>
          </div>
        </div>

        {busMode === "csv" && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4">
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onCsv} className="hidden" data-testid="csv-input" />
            <Button type="button" data-testid="csv-upload-btn" onClick={() => fileRef.current?.click()} className="gap-2 bg-blue-600 hover:bg-blue-500"><Upload className="h-4 w-4" /> {t("createProject.chooseCsv")}</Button>
            <Button type="button" data-testid="csv-template-btn" onClick={downloadTemplate} variant="outline" className="gap-2 border-slate-300"><FileDown className="h-4 w-4" /> {t("createProject.downloadTemplate")}</Button>
            <span className="text-xs text-slate-500">{t("createProject.csvColumns")}</span>
          </div>
        )}

        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`bus-row-${i}`}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.3fr_auto]">
                <Input data-testid={`bus-number-${i}`} placeholder={t("createProject.busNumber")} value={r.bus_number} onChange={(e) => update(i, "bus_number", e.target.value)} className="border-slate-300 bg-white" />
                <Input data-testid={`bus-vin-${i}`} placeholder={t("createProject.vin")} value={r.vin} onChange={(e) => update(i, "vin", e.target.value)} className="border-slate-300 bg-white" />
                <Button data-testid={`remove-row-${i}`} onClick={() => removeRow(i)} size="icon" variant="ghost" disabled={rows.length === 1} className="justify-self-end text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Select value={r.bus_type} onValueChange={(v) => update(i, "bus_type", v)}>
                  <SelectTrigger data-testid={`bus-type-${i}`} className="border-slate-300 bg-white"><SelectValue placeholder={t("createProject.busType")} /></SelectTrigger>
                  <SelectContent>{BUS_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid={`bus-manufacturer-${i}`} placeholder={t("createProject.manufacturer")} value={r.manufacturer} onChange={(e) => update(i, "manufacturer", e.target.value)} className="border-slate-300 bg-white" />
                <Select value={r.energy_source} onValueChange={(v) => update(i, "energy_source", v)}>
                  <SelectTrigger data-testid={`bus-energy-${i}`} className="border-slate-300 bg-white"><SelectValue placeholder={t("createProject.energySource")} /></SelectTrigger>
                  <SelectContent>{ENERGY_SOURCES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>

        {busMode === "manual" && (
          <Button data-testid="add-bus-row" onClick={addRow} size="sm" variant="outline" className="mt-3 gap-1.5 border-slate-300"><Plus className="h-4 w-4" /> {t("createProject.addBus")}</Button>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-6 rounded-md bg-blue-50 p-4">
          <div className="flex items-center gap-2 text-blue-700"><Package className="h-5 w-5" /><span className="overline">{t("createProject.provisioningEngine")}</span></div>
          <div><span className="font-heading text-2xl font-black text-slate-900">{rows.length}</span> <span className="text-sm text-slate-500">{t("createProject.baseUnits")}</span></div>
          <div className="text-slate-400">{t("createProject.buffer")}</div>
          <div><span className="font-heading text-2xl font-black text-emerald-600" data-testid="shipped-preview">{shipped}</span> <span className="text-sm text-slate-500">{t("createProject.unitsShipped")}</span></div>
        </div>
        <p className="mt-3 text-xs text-slate-500">{t("createProject.serialLater")}</p>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/projects")} className="border-slate-300">{t("createProject.cancel")}</Button>
        <Button data-testid="submit-project" onClick={submit} disabled={saving} className="gap-2 bg-blue-600 px-8 hover:bg-blue-500">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("createProject.createAllocate")}
        </Button>
      </div>

      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">{t("createProject.quickAddCustomer")}</DialogTitle>
            <DialogDescription>{t("createProject.quickAddDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="overline text-slate-500">{t("createProject.firstName")}</Label><Input data-testid="quick-cust-first" value={newCust.first_name} onChange={(e) => setCust("first_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("createProject.lastName")}</Label><Input data-testid="quick-cust-last" value={newCust.last_name} onChange={(e) => setCust("last_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            </div>
            <div><Label className="overline text-slate-500">{t("createProject.businessName")}</Label><Input data-testid="quick-cust-name" value={newCust.business_name} onChange={(e) => setCust("business_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="overline text-slate-500">{t("createProject.email")}</Label><Input data-testid="quick-cust-email" type="email" value={newCust.email} onChange={(e) => setCust("email", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("createProject.phone")}</Label><Input data-testid="quick-cust-phone" value={newCust.phone} onChange={(e) => setCust("phone", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            </div>
            <div><Label className="overline text-slate-500">{t("createProject.addr1")}</Label><Input data-testid="quick-cust-addr1" value={newCust.address_line1} onChange={(e) => setCust("address_line1", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            <div><Label className="overline text-slate-500">{t("createProject.addr2")}</Label><Input data-testid="quick-cust-addr2" value={newCust.address_line2} onChange={(e) => setCust("address_line2", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="overline text-slate-500">{t("createProject.city")}</Label><Input data-testid="quick-cust-city" value={newCust.city} onChange={(e) => setCust("city", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("createProject.state")}</Label><Input data-testid="quick-cust-state" value={newCust.state} onChange={(e) => setCust("state", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="overline text-slate-500">{t("createProject.country")}</Label><Input data-testid="quick-cust-country" value={newCust.country} onChange={(e) => setCust("country", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("createProject.zip")}</Label><Input data-testid="quick-cust-zip" value={newCust.postal_code} onChange={(e) => setCust("postal_code", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="quick-cust-save" onClick={quickCustomer} disabled={savingCust || !newCust.business_name} className="bg-blue-600 hover:bg-blue-500">
              {savingCust ? <Loader2 className="h-4 w-4 animate-spin" /> : t("createProject.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
